import React, {useMemo, useCallback} from 'react';
import {Font} from '@pepsico-ds/ui';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {activityHistoryColumns} from '@/services/MockData/ActivityHistory/ActivityHistoryColConfig';
import SearchCard from '@/components/atoms/SearchCard';
import StoreSelection from '@/components/atoms/StoreSelection';
import {useActivityHistory} from '@/hooks/useActivityHistory';
import {useAppDispatch} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import './ActivityHistory.scss';

const ActivityHistory = () => {
  const dispatch = useAppDispatch();

  const showToast = useCallback(
    (message, type = 'error') => {
      dispatch(
        setToastNotifications({
          show: true,
          message: message,
          type: type
        })
      );
    },
    [dispatch]
  );

  const {
    searchBy,
    searchInput,
    tableData,
    showStores,
    storesList,
    isLoading,
    errorMessage,
    totalRecords,
    isSearchEnabled,
    handleSearchByChange,
    handleSearchInputChange,
    handleSearch,
    handleStoreSelect,
    handleDownload,
    getPlaceholder,
    setSortingObj,
    sortingObj,
    setPageIndex,
    pageIndex
  } = useActivityHistory(showToast);

  const handleDownloadWithSelection = useCallback(
    (selectedRows) => {
      handleDownload(selectedRows);
    },
    [handleDownload]
  );

  const columns = useMemo(() => activityHistoryColumns, []);

  const renderContent = () => {
    if ((tableData && tableData.length > 0) || isLoading) {
      return (
        <PeacockDataTable
          columns={columns}
          records={tableData || []}
          isLoading={isLoading}
          pageSize={10}
          totalRecords={totalRecords}
          paginationConfig={{
            pageIndex,
            setPageIndex
          }}
          sortingConfig={{
            handleSorting: setSortingObj,
            initialSortConfig: sortingObj
          }}
          filterConfig={{
            disabledHeaders: ['id', 'type']
          }}
          toolbarConfig={{
            showDownload: tableData && tableData.length > 0,
            onDownload: handleDownloadWithSelection
          }}
        />
      );
    }

    if (searchBy === '' || searchInput.trim() === '') {
      return (
        <div className='no-data-message' data-testid='no-data-message'>
          <Font variant='body1' className='no-data-text'>
            Search for a user to view activity history.
          </Font>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className='activity-history-container'
      data-testid='activity-history-container'>
      <Font variant='h4' className='activity-history-header'>
        Activity History
      </Font>

      <SearchCard
        searchBy={searchBy}
        searchInput={searchInput}
        errorMessage={errorMessage}
        isSearchEnabled={isSearchEnabled}
        onSearchByChange={handleSearchByChange}
        onSearchInputChange={handleSearchInputChange}
        onSearch={handleSearch}
        getPlaceholder={getPlaceholder}
        data-testid='search-card'
      />

      {showStores && (
        <StoreSelection
          storesList={storesList}
          onStoreSelect={handleStoreSelect}
          dataTestId='store-selection-card'
        />
      )}

      {/* Data Table */}
      {renderContent()}
    </div>
  );
};

export default React.memo(ActivityHistory);
